import { CommandBarItem } from '../types'

const EMPTY_QUERY_SCORE = 1
const TITLE_MATCH_BOOST = 25
const SUBTITLE_MATCH_BOOST = 10

export const normalizeCommandBarSearchText = (value: string) =>
  value.trim().toLowerCase()

const scoreValue = (value: string, query: string): number => {
  const normalizedValue = normalizeCommandBarSearchText(value)

  if (!normalizedValue) {
    return 0
  }

  if (normalizedValue === query) {
    return 100
  }

  if (normalizedValue.startsWith(query)) {
    return 80
  }

  if (normalizedValue.split(/\s+/).some(word => word.startsWith(query))) {
    return 65
  }

  if (normalizedValue.includes(query)) {
    return 40
  }

  return 0
}

const scoreTitle = (value: string, query: string) => {
  const score = scoreValue(value, query)

  return score ? score + TITLE_MATCH_BOOST : 0
}

const scoreSubtitle = (value: string, query: string) => {
  const score = scoreValue(value, query)

  return score ? score + SUBTITLE_MATCH_BOOST : 0
}

export const getCommandBarItemScore = (
  item: Pick<
    CommandBarItem,
    'title' | 'subtitle' | 'keywords' | 'priority' | 'section'
  >,
  query: string
) => {
  const normalizedQuery = normalizeCommandBarSearchText(query)

  if (!normalizedQuery) {
    return EMPTY_QUERY_SCORE + (item.priority || 0)
  }

  const scores = [
    scoreTitle(item.title, normalizedQuery),
    scoreSubtitle(item.subtitle || '', normalizedQuery),
    ...(item.keywords || []).map(keyword =>
      scoreValue(keyword, normalizedQuery)
    ),
  ]
  const score = Math.max(...scores)

  if (!score) {
    return 0
  }

  return score + (item.priority || 0)
}

export const getVisibleCommandBarItems = (
  items: CommandBarItem[],
  query: string
) =>
  items
    .map((item, index) => ({
      item,
      index,
      score: getCommandBarItemScore(item, query),
    }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score
      }

      return a.index - b.index
    })
    .map(({ item }) => item)

export const getQueryLengthBucket = (query: string) => {
  const length = normalizeCommandBarSearchText(query).length

  if (length === 0) {
    return '0'
  }

  if (length <= 2) {
    return '1-2'
  }

  if (length <= 5) {
    return '3-5'
  }

  return '6+'
}
